// ✅ src/pages/VehicleV2/AddNewTyre.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/addNewTyre", addNewTyre);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/addNewTyre/config
//   GET  https://nissi-india.com/api/addNewTyre/dropdowns?branchId=..&branchType=..&addTyreVI=..
//   GET  https://nissi-india.com/api/addNewTyre/view?tyreId=..            (edit mode)
//   GET  https://nissi-india.com/api/addNewTyre/tyre-serials?vehicleId=..&branchId=.. (only if addTyreVI=1)
//   GET  https://nissi-india.com/api/addNewTyre/tyre-detail?storeIssueDetId=..&serialNo=.. (only if addTyreVI=1)
//   POST https://nissi-india.com/api/addNewTyre/upload                    (multipart, field "document")
//   POST https://nissi-india.com/api/addNewTyre/save
//
// ✅ Updated:
// - Sends x-finyear / x-finyearid headers for inventory DB resolver
// - Location is auto-set to logged-in user's branchId and DISABLED
// - Payload forces locationId = branchId

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaUpload, FaTrash } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addNewTyre";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

// Convert "dd/MM/yyyy" -> "yyyy-MM-dd" (for input[type=date])
const ddmmyyyyToISO = (s) => {
  if (!s) return "";
  const txt = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(txt)) return txt;
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(txt)) return "";
  const [dd, mm, yyyy] = txt.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const AddNewTyre = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const tyreId = useMemo(() => toInt(searchParams.get("tyreId")), [searchParams]);

  // localStorage values
  const loginUserId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType = localStorage.getItem("branchtype") || "HO";
  const branchId = Number(localStorage.getItem("branchid") || 8);

  // ✅ for inventory resolver headers
  const finYear =
    localStorage.getItem("financialYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("finYear") ||
    "";

  const finYearId =
    localStorage.getItem("financialYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("finYearId") ||
    localStorage.getItem("financialyearid") ||
    "";

  // config
  const [addTyreVI, setAddTyreVI] = useState(0);

  // dropdowns
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [purchaseFromList, setPurchaseFromList] = useState([]);
  const [positions, setPositions] = useState([]);

  // serial dropdown
  const [serialOptions, setSerialOptions] = useState([]);

  // documents
  const [docs, setDocs] = useState([]);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    typeSizePlyRating: "",
    typeMake: "",
    purchaseDate: "",
    typeAmount: "",
    locationId: branchId ? String(branchId) : "",

    vehicleId: "",
    typeSerialNo: "",
    storeIssueDetId: "",
    storeIssueSerialText: "",

    position: "",
    typeModel: "",

    purchaseFrom: "",
    purchaseFromNew: "",
  });

  // ✅ axios headers for inventory db resolver
  const inventoryHeaders = useMemo(() => {
    const headers = {};

    if (toStr(finYear)) headers["x-finyear"] = toStr(finYear);
    if (toStr(finYearId)) {
      headers["x-finyearid"] = toStr(finYearId);
      headers["x-finyear-id"] = toStr(finYearId);
    }

    return headers;
  }, [finYear, finYearId]);

  const axiosConfigWithHeaders = useMemo(
    () => ({
      headers: inventoryHeaders,
    }),
    [inventoryHeaders]
  );

  // keep location always = branchId
  useEffect(() => {
    setFormData((p) => ({
      ...p,
      locationId: branchId ? String(branchId) : "",
    }));
  }, [branchId]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "locationId") return;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /** ---------------- Load config + dropdowns + edit ---------------- */
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        // 1) config
        const cfg = await axios.get(`${API_BASE}/config`, axiosConfigWithHeaders);
        const vi = toInt(cfg.data?.addTyreVI, 0);
        setAddTyreVI(vi);

        // 2) dropdowns
        const dd = await axios.get(`${API_BASE}/dropdowns`, {
          params: { branchId, branchType, addTyreVI: vi },
          headers: inventoryHeaders,
        });

        setLocations(dd.data?.locations || []);
        setVehicles(dd.data?.vehicles || []);
        setPurchaseFromList(dd.data?.purchaseFrom || []);
        setPositions(dd.data?.positions || []);

        setFormData((p) => ({
          ...p,
          locationId: branchId ? String(branchId) : "",
        }));

        // 3) edit mode
        if (tyreId > 0) {
          const v = await axios.get(`${API_BASE}/view`, {
            params: { tyreId },
            headers: inventoryHeaders,
          });

          const t = v.data?.tyre;
          const docsResp = v.data?.documents || [];

          if (t) {
            const dateReceiptISO = ddmmyyyyToISO(t.datereceipt);
            const purchaseISO = ddmmyyyyToISO(t.Purchasedate);

            setDocs(docsResp.map((d) => d.file).filter(Boolean));

            setFormData((p) => ({
              ...p,
              date: dateReceiptISO || p.date,
              typeSizePlyRating: t.fld_Tyresize || "",
              typeMake: t.fld_TyreMake || "",
              purchaseDate: purchaseISO || "",
              typeAmount: t.fld_TyreAmounts || "",
              locationId: branchId ? String(branchId) : "",
              vehicleId: String(t.fld_vehicleid ?? ""),
              position: t.fld_TyrePosition || "",
              typeModel: t.fld_TyreModel || "",
              purchaseFrom: t.fld_Purchasefrom || "",
              typeSerialNo: t.fld_Tyreserialno || "",
              storeIssueSerialText: t.fld_Tyreserialno || "",
            }));
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [tyreId, branchId, branchType, axiosConfigWithHeaders, inventoryHeaders]);

  /** ---------------- When vehicle changes in VI mode -> load serial dropdown ---------------- */
  useEffect(() => {
    const loadSerials = async () => {
      try {
        if (addTyreVI !== 1) return;

        const vehId = toInt(formData.vehicleId);
        if (!vehId) {
          setSerialOptions([]);
          setFormData((p) => ({
            ...p,
            storeIssueDetId: "",
            storeIssueSerialText: "",
          }));
          return;
        }

        const rs = await axios.get(`${API_BASE}/tyre-serials`, {
          params: { vehicleId: vehId, branchId },
          headers: inventoryHeaders,
        });

        setSerialOptions(rs.data?.serials || []);

        setFormData((p) => ({
          ...p,
          storeIssueDetId: "",
          storeIssueSerialText: "",
          typeMake: "",
          typeSizePlyRating: "",
          typeAmount: "",
          purchaseDate: "",
          purchaseFrom: "",
          purchaseFromNew: "",
        }));
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serials");
      }
    };

    loadSerials();
  }, [formData.vehicleId, addTyreVI, branchId, inventoryHeaders]);

  /** ---------------- When serial changes in VI mode -> load tyre details ---------------- */
  useEffect(() => {
    const loadTyreDetail = async () => {
      try {
        if (addTyreVI !== 1) return;

        const sid = toInt(formData.storeIssueDetId);
        const serialText = formData.storeIssueSerialText;
        if (!sid || !serialText) return;

        const rs = await axios.get(`${API_BASE}/tyre-detail`, {
          params: { storeIssueDetId: sid, serialNo: serialText },
          headers: inventoryHeaders,
        });

        const d = rs.data?.detail;
        if (d) {
          setFormData((p) => ({
            ...p,
            typeMake: d.make || "",
            typeSizePlyRating: d.partNo || "",
            typeAmount: d.price || "",
            purchaseFrom: d.supplierName || "",
            purchaseDate: ddmmyyyyToISO(d.issueDate) || "",
          }));
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load tyre detail");
      }
    };

    loadTyreDetail();
  }, [formData.storeIssueDetId, formData.storeIssueSerialText, addTyreVI, inventoryHeaders]);

  /** ---------------- Upload document ---------------- */
  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      const fd = new FormData();
      fd.append("document", file);

      const rs = await axios.post(`${API_BASE}/upload`, fd, {
        headers: {
          ...inventoryHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      const filename = rs.data?.filename;
      if (filename) {
        setDocs((p) => [...p, filename]);
        toast.success("File uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const removeDoc = (idx) => setDocs((p) => p.filter((_, i) => i !== idx));

  /** ---------------- Save ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date) return toast.error("Date is required");
    if (!formData.typeSizePlyRating) return toast.error("Type Size/Ply Rating is required");
    if (!formData.typeMake) return toast.error("Type Make is required");
    if (!formData.purchaseDate) return toast.error("Purchase Date is required");
    if (!formData.vehicleId) return toast.error("Vehicle Code is required");
    if (!formData.position) return toast.error("Position is required");

    let serialNoToSend = "";
    let storeIssueDetIdToSend = 0;

    if (addTyreVI === 1) {
      if (!formData.storeIssueDetId || !formData.storeIssueSerialText) {
        return toast.error("Type Serial No is required");
      }
      serialNoToSend = formData.storeIssueSerialText;
      storeIssueDetIdToSend = toInt(formData.storeIssueDetId);
    } else {
      if (!formData.typeSerialNo) return toast.error("Type Serial No is required");
      serialNoToSend = formData.typeSerialNo;
    }

    const purchaseFromMode = String(formData.purchaseFrom || "").trim() === "New" ? "NEW" : "EXISTING";

    const payload = {
      tyreId: tyreId > 0 ? tyreId : 0,
      branchId,
      branchType,
      addTyreVI,

      locationId: toInt(branchId),
      date: formData.date,
      vehicleId: toInt(formData.vehicleId),

      tyreSerialNo: serialNoToSend,
      storeIssueDetId: storeIssueDetIdToSend,

      tyreSize: formData.typeSizePlyRating,
      position: formData.position,
      tyreMake: formData.typeMake,
      tyreModel: formData.typeModel,
      purchaseDate: formData.purchaseDate,
      purchaseFromMode,
      purchaseFrom: formData.purchaseFrom,
      purchaseFromNew: formData.purchaseFromNew,

      amount: formData.typeAmount,
      loginUserId,
      ipAddress: "",
      documents: docs,
    };

    try {
      setLoading(true);

      const rs = await axios.post(`${API_BASE}/save`, payload, {
        headers: inventoryHeaders,
      });

      if (rs.data?.success) {
        toast.success(rs.data?.message || "Saved");
        navigate("/vehicle/TyreDetailsHome");
      } else {
        toast.error(rs.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDocs([]);
    setSerialOptions([]);

    setFormData({
      date: new Date().toISOString().split("T")[0],
      typeSizePlyRating: "",
      typeMake: "",
      purchaseDate: "",
      typeAmount: "",
      locationId: branchId ? String(branchId) : "",

      vehicleId: "",
      typeSerialNo: "",
      storeIssueDetId: "",
      storeIssueSerialText: "",

      position: "",
      typeModel: "",

      purchaseFrom: "",
      purchaseFromNew: "",
    });
  };

  const handleCancel = () => navigate(-1);

  const lockedLocationClass = `${selectClass} ${isDark ? "opacity-80" : "opacity-90"} cursor-not-allowed`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD NEW TYRE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-4">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {tyreId > 0 ? "Edit Tyre" : "Add New Tyre"}
                </h1>
                {loading && (
                  <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FaTimes className="w-4 h-4" />
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSave className="w-4 h-4" />
                  {tyreId > 0 ? "Update Tyre" : "Save Tyre"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Date *</label>
                  <input type="date" name="date" value={formData.date} onChange={handleInputChange} className={inputClass} />
                </div>

                <div>
                  <label className={labelClass}>Type Size/Ply Rating *</label>
                  <input
                    type="text"
                    name="typeSizePlyRating"
                    value={formData.typeSizePlyRating}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter type size/ply rating"
                  />
                </div>

                <div>
                  <label className={labelClass}>Type Make *</label>
                  <input
                    type="text"
                    name="typeMake"
                    value={formData.typeMake}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre make"
                  />
                </div>

                <div>
                  <label className={labelClass}>Purchase Date *</label>
                  <input
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Type Amount *</label>
                  <input
                    type="number"
                    name="typeAmount"
                    value={formData.typeAmount}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className={labelClass}>Location *</label>
                  <select
                    name="locationId"
                    value={formData.locationId}
                    onChange={handleInputChange}
                    className={lockedLocationClass}
                    disabled
                    title="Location locked to your branch"
                  >
                    <option value="">Select</option>
                    {locations.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className={labelClass}>Upload Document</label>
                  <div className="flex items-center gap-4">
                    <label className={`flex-1 ${inputClass} cursor-pointer py-3 flex items-center justify-center gap-2`}>
                      <FaUpload className="w-4 h-4" />
                      Choose File
                      <input type="file" onChange={handleFileUpload} className="hidden" />
                    </label>
                  </div>

                  {docs.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {docs.map((d, idx) => (
                        <div
                          key={`${d}-${idx}`}
                          className={`flex items-center justify-between px-3 py-2 rounded border ${
                            isDark ? "border-gray-700 bg-gray-800/40" : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <a
                            href={`https://nissi-india.com/InvoiceDetails/${encodeURIComponent(d)}`}
                            target="_blank"
                            rel="noreferrer"
                            className={`text-sm underline ${isDark ? "text-gray-200" : "text-gray-700"}`}
                            title="Open"
                          >
                            {d}
                          </a>
                          <button type="button" onClick={() => removeDoc(idx)} className="text-red-500 hover:text-red-600">
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Vehicle Code *</label>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.code}
                      </option>
                    ))}
                  </select>
                </div>

                {addTyreVI === 1 ? (
                  <div>
                    <label className={labelClass}>Type Serial No *</label>
                    <select
                      name="storeIssueDetId"
                      value={formData.storeIssueDetId}
                      onChange={(e) => {
                        const val = e.target.value;
                        const opt = serialOptions.find((x) => String(x.id) === String(val));
                        setFormData((p) => ({
                          ...p,
                          storeIssueDetId: val,
                          storeIssueSerialText: opt?.text || "",
                        }));
                      }}
                      className={selectClass}
                    >
                      <option value="">Select</option>
                      {serialOptions.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.text}
                        </option>
                      ))}
                    </select>

                    {tyreId > 0 && formData.storeIssueSerialText && (
                      <div className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                        Current Serial: {formData.storeIssueSerialText}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className={labelClass}>Type Serial No *</label>
                    <input
                      type="text"
                      name="typeSerialNo"
                      value={formData.typeSerialNo}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter serial number"
                    />
                  </div>
                )}

                <div>
                  <label className={labelClass}>Position *</label>
                  <select name="position" value={formData.position} onChange={handleInputChange} className={selectClass}>
                    <option value="">Select</option>
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Type Model</label>
                  <input
                    type="text"
                    name="typeModel"
                    value={formData.typeModel}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre model"
                  />
                </div>

                <div>
                  <label className={labelClass}>Purchase From *</label>
                  <select
                    name="purchaseFrom"
                    value={formData.purchaseFrom}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((p) => ({
                        ...p,
                        purchaseFrom: val,
                        purchaseFromNew: val === "New" ? p.purchaseFromNew : "",
                      }));
                    }}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {purchaseFromList.map((p) => (
                      <option key={p.id ?? p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                    <option value="New">New</option>
                  </select>

                  {formData.purchaseFrom === "New" && (
                    <div className="mt-2">
                      <label className={labelClass}>New Purchase From</label>
                      <input
                        type="text"
                        name="purchaseFromNew"
                        value={formData.purchaseFromNew}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Enter new supplier"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`my-8 border-t ${isDark ? "border-gray-700" : "border-gray-300"}`} />

            <div className="flex justify-center gap-4 mt-8">
              <button
                type="button"
                onClick={handleReset}
                className={`px-8 py-3 border rounded-lg font-medium ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Reset Form
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`px-8 py-3 ${buttonClass} ${hoverClass} text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {tyreId > 0 ? "Update Tyre Details" : "Save Tyre Details"}
              </button>
            </div>
          </form>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddNewTyre;